import React from 'react';
import {
  X,
  ExternalLink,
  Calendar as CalendarIcon,
  Building2,
  CheckCircle2,
  Clock,
  AlertCircle,
  Star,
  Edit2,
  Trash2,
  MapPin,
  User,
  Share2,
  Link2,
} from 'lucide-react';
import { format } from 'date-fns';
import { CryptoEvent, UserProfile, CATEGORY_BY_VALUE, submitterLabel } from '../types';
import { trackingUrlFor } from '../supabase';

interface EventDetailModalProps {
  event: CryptoEvent;
  submitter?: UserProfile;
  onClose: () => void;
  isAdmin: boolean;
  currentUserUid?: string;
  onEdit: (event: CryptoEvent) => void;
  onDelete: (eventId: string) => void;
  onApprove?: (eventId: string) => void;
  onReject?: (eventId: string) => void;
}

const ShareRow: React.FC<{ event: CryptoEvent }> = ({ event }) => {
  const [copied, setCopied] = React.useState(false);
  const url = `${window.location.origin}/event/${event.id}`;
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, url });
        return;
      } catch {
        // fall through to clipboard
      }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleShare}
      className="w-full h-11 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors inline-flex items-center justify-center gap-2"
    >
      {copied ? <Link2 className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
      {copied ? 'Link copied' : 'Share event'}
    </button>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'approved')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 pill-outline text-[10px] font-bold uppercase tracking-wider">
        <CheckCircle2 className="w-3 h-3 text-[#14f195]" />
        Approved
      </span>
    );
  if (status === 'pending')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 pill-outline text-[10px] font-bold uppercase tracking-wider">
        <Clock className="w-3 h-3 text-[#ff6b4d]" />
        Pending
      </span>
    );
  if (status === 'rejected')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 pill-outline text-[10px] font-bold uppercase tracking-wider">
        <AlertCircle className="w-3 h-3 text-[#ff6a6a]" />
        Rejected
      </span>
    );
  return null;
};

export const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  submitter,
  onClose,
  isAdmin,
  currentUserUid,
  onEdit,
  onDelete,
  onApprove,
  onReject,
}) => {
  const canManage = isAdmin || currentUserUid === event.createdBy;
  const isPending = event.status === 'pending';
  const cat = CATEGORY_BY_VALUE[event.category];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center safe-overlay bg-[#0b0b0c]/40 overflow-y-auto">
      <div className="sticker bg-[#e9dffa] w-full max-w-xl sm:my-8 mobile-sheet overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 sm:p-6 border-b border-[#0b0b0c]/15">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="px-2.5 py-1 rounded-full border-[1.5px] border-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: cat.fill, color: cat.textColor === 'white' ? '#fff' : '#0b0b0c' }}
            >
              {cat.label}
            </span>
            {event.chain && (
              <span className="px-2.5 py-1 pill-outline text-[10px] font-bold uppercase tracking-wider">
                {event.chain === 'General Crypto' ? 'Industry-wide' : event.chain}
              </span>
            )}
            <StatusBadge status={event.status} />
            {event.featured && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 pill-outline text-[10px] font-bold uppercase tracking-wider">
                <Star className="w-3 h-3 fill-[#ffd24a] text-[#0b0b0c]" />
                Featured
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-11 h-11 sm:w-9 sm:h-9 rounded-full border-[1.5px] border-[#0b0b0c] bg-white hover:bg-[#0b0b0c] hover:text-white flex items-center justify-center transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto custom-scrollbar flex-1 p-4 sm:p-6 space-y-5">
          {canManage && (
            <div className="flex justify-end gap-2">
              <button
                onClick={() => onEdit(event)}
                className="w-11 h-11 sm:w-9 sm:h-9 rounded-full border-[1.5px] border-[#0b0b0c] bg-white hover:bg-[#0b0b0c] hover:text-white flex items-center justify-center transition-colors"
                title="Edit event"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(event.id)}
                className="w-11 h-11 sm:w-9 sm:h-9 rounded-full border-[1.5px] border-[#0b0b0c] bg-[#ff6b4d] text-white hover:bg-[#ff6a6a] flex items-center justify-center transition-colors"
                title="Delete event"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}

          <h2 className="font-bold text-2xl sm:text-4xl uppercase tracking-tight text-[#0b0b0c] leading-[0.95]">
            {event.title}
          </h2>

          {event.companyName && (
            <div className="flex items-center gap-2 text-sm font-bold text-[#0b0b0c]">
              <Building2 className="w-4 h-4 text-[#0b0b0c]/55" />
              {event.companyName}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sticker-soft bg-white p-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#0b0b0c]/55 mb-1">
                <CalendarIcon className="w-3 h-3" />
                Date
              </div>
              <div className="text-sm font-bold text-[#0b0b0c]">
                {format(event.date, 'MMM d, yyyy')}
              </div>
              {event.time && <div className="text-xs text-[#0b0b0c]/65 mt-0.5">{event.time}</div>}
            </div>
            <div className="sticker-soft bg-white p-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#0b0b0c]/55 mb-1">
                <User className="w-3 h-3" />
                Submitter
              </div>
              <div className="text-sm font-bold text-[#0b0b0c] truncate">
                {submitterLabel(event, submitter)}
              </div>
            </div>
          </div>

          {event.location && (
            <div className="sticker-soft bg-white p-3 flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#0b0b0c]/55 mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#0b0b0c]/55">
                  Location
                </div>
                <div className="text-sm font-medium text-[#0b0b0c]">{event.location}</div>
              </div>
            </div>
          )}

          <div>
            <h3 className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55 mb-2">
              Description
            </h3>
            <p className="text-sm text-[#0b0b0c]/75 leading-relaxed">
              {event.description || 'No description provided for this event.'}
            </p>
          </div>

          {event.rejectionReason && canManage && event.status === 'rejected' && (
            <div className="sticker-soft bg-white p-4">
              <h4 className="font-bold text-[10px] uppercase tracking-wider text-[#ff6a6a] mb-1">
                Admin feedback
              </h4>
              <p className="text-sm text-[#0b0b0c]/75 italic">"{event.rejectionReason}"</p>
            </div>
          )}

          {event.link && (
            <div className="flex flex-col gap-2">
              <a
                href={trackingUrlFor(event.linkSlug, { utm_source: 'calendar', utm_medium: 'event_modal' }) ?? event.link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-12 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#5b29a6] transition-colors inline-flex items-center justify-center gap-2"
              >
                Visit event site
                <ExternalLink className="w-4 h-4" />
              </a>
              <ShareRow event={event} />
            </div>
          )}
          {!event.link && <ShareRow event={event} />}

          {isPending && (onApprove || onReject) && (
            <div className="flex flex-col sm:flex-row gap-3 pt-3 border-t border-[#0b0b0c]/15">
              {onApprove && (
                <button
                  onClick={() => onApprove(event.id)}
                  className="flex-1 h-11 rounded-full bg-[#14f195] text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Approve
                </button>
              )}
              {onReject && (
                <button
                  onClick={() => onReject(event.id)}
                  className="flex-1 h-11 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors inline-flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Reject
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
